// Routine run commands. Pure: (ctx, input) → { writes, run, hooks }. One group each, so a run and its clock move
// together. ctx = { routines, runs (processExecutions), tasks, sessions, clock, timer, lock, projects, departments,
// projectTypes, uid, now, newId } — only what a command reads.
//
// Starting a routine clocks into its project — the routine's own, or the one picked at start when it asks
// (projectMode 'ask'; routineSetProject changes it mid-run) — and puts the first step on the timer; finishing puts
// the clock back the way it was (off, or back on the job it interrupted).
// `hooks` = what just happened ({ when: start | step | skip | finish | skipRun, … }) — the automation hook points;
// the app runs them after the group commits (nothing is wired to them yet).
import { normalizeRoutine, buildRun, runTasks, runsOn, nextSlot, currentStep, runMetrics, evaluateRunGoals, runOutcome, runIdFor, scheduleOccurrences, stepTaskId, runProject } from '../routines.js';
import { makeTaskDoc } from '../taskdoc.js';
import { makeTimerState, bankable } from '../timer.js';
import { localDateStr } from '../time.js';
import { applyWrites, del, mergeUpdates, set, update } from './writes.js';
import { buildTimeEntry, completeTask, stopTask, reopenTask } from './tasks.js';
import { clockIn, clockOut, switchProject, moveSession } from './clock.js';
import { startTask, pauseTask, resumeTask } from './timer.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const RUN = (id) => `processExecutions/${id}`;
const nextRunN = (slots) => (slots || []).reduce((m, x) => Math.max(m, x.n || 1), 0) + 1;

function needRoutine(ctx, id) {
  const raw = (ctx.routines || []).find((x) => x.id === id);
  if (!raw) throw new Error(`no routine ${id}`);
  return normalizeRoutine(raw);
}
function needRun(ctx, id) {
  const r = (ctx.runs || []).find((x) => x.id === id);
  if (!r) throw new Error(`no routine run ${id}`);
  return r;
}

/** ctx after `writes` (tasks, sessions, clock, timer, lock, runs) — so the next command in the same group sees them. */
function advance(ctx, writes) {
  const docs = new Map();
  for (const t of ctx.tasks || []) docs.set(`tasks/${t.id}`, t);
  for (const s of ctx.sessions || []) docs.set(`clockSessions/${s.id}`, s);
  for (const r of ctx.runs || []) docs.set(RUN(r.id), r);
  for (const k of ['timer', 'clock', 'lock']) if (ctx[k]) docs.set(`meta/${k}`, ctx[k]);
  const next = applyWrites(docs, writes);
  const pick = (pre) => [...next.entries()].filter(([k]) => k.startsWith(pre)).map(([k, v]) => ({ id: k.slice(pre.length), ...v }));
  return { ...ctx, tasks: pick('tasks/'), sessions: pick('clockSessions/'), runs: pick('processExecutions/'), timer: next.get('meta/timer') || null, clock: next.get('meta/clock') || null, lock: next.get('meta/lock') || null };
}

/**
 * Today's (or `date`'s) run for a routine, created if it isn't there: the run doc + its step tasks, deterministic ids
 * (so two devices doing this at once make one run). Returns { writes, run, created }.
 */
export function materializeRun(ctx, { routineId, date = null, n = null, trigger = null, dueAt = null, projectId }) {
  const r = needRoutine(ctx, routineId);
  const at = now(ctx);
  const day = date || localDateStr(at);
  const slot = n || 1;
  const existing = (ctx.runs || []).find((x) => x.id === runIdFor(r.id, day, slot));
  if (existing) return { writes: [], run: existing, created: false };
  const run = buildRun(r, { date: day, n: slot, trigger: trigger || { type: 'manual', at }, dueAt, uid: ctx.uid || null, now: at, projectId });
  if (!r.steps.length) throw new Error(`${r.name} has no steps yet`);
  const tasks = runTasks(r, run, at);
  return { writes: [set(RUN(run.id), run), ...tasks.map((t) => set(`tasks/${t.id}`, t))], run, created: true, tasks };
}

/**
 * Start a routine now: its run (made if needed), clocked into its project, the first step on the timer.
 * A run already going is simply resumed. input = { routineId, date?, n?, runId?, trigger?, projectId? }
 * projectId = the project picked for this run (a routine that asks); without one it starts on the pre-pick (or no
 * project) and the routine mode asks. A fixed routine always uses its own project.
 */
export function startRoutine(ctx, { routineId, date = null, n = null, runId = null, trigger = null, projectId }) {
  const r = needRoutine(ctx, routineId);
  const picked = r.projectMode === 'ask' && projectId !== undefined ? (projectId || null) : undefined;
  const at = now(ctx);
  const day = date || localDateStr(at);
  // Started by hand (not a ring/trigger) when today's run is already through = run it again (a new run that day).
  const manual = !trigger || trigger.type === 'manual';
  let run = runId ? needRun(ctx, runId) : null;
  if (!run) {
    const slots = runsOn(ctx.runs, r.id, day, ctx.uid || null);
    run = n ? slots.find((x) => (x.n || 1) === n) : slots.find((x) => x.status === 'due' || x.status === 'running');
    if (!run && !n && slots.length) {
      const free = nextSlot(r, ctx.runs, day, ctx.uid || null) || (manual ? nextRunN(slots) : null);
      if (!free) throw new Error(`${r.name} is already ${slots.every((x) => x.status === 'skipped') ? 'skipped' : 'done'} today`);
      n = free;
    }
  }
  if (run?.status === 'running') {
    if (picked === undefined || (run.projectId || null) === picked) return resumeRoutine(ctx, { runId: run.id });
    const moved = routineSetProject(ctx, { runId: run.id, projectId: picked });
    const res = resumeRoutine(advance(ctx, moved.writes), { runId: run.id });
    return { ...res, writes: mergeUpdates([...moved.writes, ...res.writes]), run: { ...moved.run, pausedAt: null } };
  }
  if (run && run.status !== 'due') {
    const free = nextSlot(r, ctx.runs, day, ctx.uid || null) || (manual ? nextRunN(runsOn(ctx.runs, r.id, day, ctx.uid || null)) : null);
    if (!free) throw new Error(`${r.name} is already ${run.status === 'skipped' ? 'skipped' : 'done'} today`);
    run = null; n = free;
  }
  const writes = [];
  let c = ctx;
  if (!run) {
    const slot = n || nextSlot(r, ctx.runs, day, ctx.uid || null) || 1;
    // Due when its schedule said (so "start within … of the ring" measures from the ring), else now.
    const occ = scheduleOccurrences(r, day, day).find((o) => o.n === slot);
    const m = materializeRun(ctx, { routineId: r.id, date: day, n: slot, trigger: trigger || { type: occ ? 'schedule' : 'manual', at }, dueAt: occ && occ.at <= at ? occ.at : at, projectId: picked });
    writes.push(...m.writes);
    run = m.run;
    c = advance(c, m.writes);
  } else if (picked !== undefined && (run.projectId || null) !== picked) {
    // made earlier (its ring) on the pre-pick: the run and its steps move to the project picked now
    const moved = routineSetProject(c, { runId: run.id, projectId: picked });
    writes.push(...moved.writes);
    run = moved.run;
    c = advance(c, moved.writes);
  }
  const first = currentStep(run);
  if (!first) throw new Error(`${r.name} has nothing left to do`);
  const clockBefore = c.clock?.active ? { projectId: c.clock.projectId || null, taskId: c.timer?.taskId || null } : null;
  const pid = runProject(r, run);
  let clockWrites;
  if (!c.clock?.active) clockWrites = clockIn(c, { projectId: pid, taskId: first.taskId }).writes;
  else if ((c.clock.projectId || null) !== pid) clockWrites = switchProject(c, { projectId: pid, taskId: first.taskId }).writes;
  else clockWrites = startTask(c, { taskId: first.taskId }).writes;
  writes.push(...clockWrites);
  const steps = run.steps.map((s) => (s.stepId === first.stepId ? { ...s, startedAt: at } : s));
  const patch = { status: 'running', startedAt: run.startedAt || at, pausedAt: null, steps, clockBefore, updatedAt: at };
  writes.push(update(RUN(run.id), patch));
  const out = { ...run, ...patch };
  return { writes: mergeUpdates(writes), run: out, step: first, hooks: [{ when: 'start', routineId: r.id, runId: run.id }] };
}

/** Bank the running timer if it's on this task (for the step being closed). */
function bankStep(ctx, taskId, at) {
  const b = ctx.timer?.taskId === taskId ? bankable({ timer: ctx.timer, clockedIn: ctx.clock, taskId, now: at }) : { elapsed: 0 };
  return b.elapsed > 0 ? { elapsed: b.elapsed, timeEntry: buildTimeEntry(b.elapsed, at, ctx.newId), sessionId: ctx.clock?.sessionId || null, pauseTime: ctx.timer?.accumulatedPause || 0 } : { elapsed: 0 };
}

/** Move to the next open step (timer on it), or finish the run when there is none. */
function moveOn(ctx, r, run, at, writes, hooks) {
  const next = currentStep(run);
  // A later step ticked off (checklist) while the timer runs on the current one: leave that timer alone.
  if (next && ctx.timer?.taskId && ctx.timer.taskId === next.taskId && next.startedAt) {
    return { writes: mergeUpdates(writes), run, step: next, finished: false, hooks };
  }
  if (next) {
    const c = { ...ctx, timer: makeTimerState({ startedAt: at }) };   // the closed step is banked already
    const w = ctx.tasks.some((t) => t.id === next.taskId && t.status !== 'done') ? startTask(c, { taskId: next.taskId }).writes : [];
    writes.push(...w);
    const steps = run.steps.map((s) => (s.stepId === next.stepId ? { ...s, startedAt: at } : s));
    const patch = { steps, updatedAt: at };
    writes.push(update(RUN(run.id), patch));
    return { writes: mergeUpdates(writes), run: { ...run, ...patch }, step: next, finished: false, hooks };
  }
  const c = advance(ctx, writes);
  const f = finishRoutine({ ...c, runs: (c.runs || []).map((x) => (x.id === run.id ? run : x)) }, { runId: run.id, banked: true });
  return { writes: mergeUpdates([...writes, ...f.writes]), run: f.run, step: null, finished: true, hooks: [...hooks, ...f.hooks] };
}

/** The current (or given) step is done — its task done with its time, a captured value kept, on to the next. */
export function routineStepDone(ctx, { runId, stepId = null, value }) {
  const run = needRun(ctx, runId);
  if (run.status !== 'running') throw new Error('that routine isn’t running — start it first');
  const r = needRoutine(ctx, run.routineId);
  const step = stepId ? run.steps.find((s) => s.stepId === stepId) : currentStep(run);
  if (!step) throw new Error('no step to finish');
  if (step.doneAt) throw new Error(`${step.title} is already done`);
  const at = now(ctx);
  const writes = [];
  const task = (ctx.tasks || []).find((t) => t.id === step.taskId);
  if (task && task.status !== 'done') writes.push(...completeTask(ctx, { id: task.id, ...bankStep(ctx, task.id, at) }).writes);
  const c = advance(ctx, writes);
  const steps = run.steps.map((s) => (s.stepId === step.stepId ? { ...s, startedAt: s.startedAt || at, doneAt: at, skipped: false, ...(value !== undefined ? { value } : {}) } : s));
  const next = { ...run, steps };
  writes.push(update(RUN(run.id), { steps, updatedAt: at }));
  return moveOn(c, r, next, at, writes, [{ when: 'step', routineId: r.id, runId, stepId: step.stepId }]);
}

/** Skip a step: its time (if any) is kept on its task, an untouched task is removed; on to the next. */
export function routineSkipStep(ctx, { runId, stepId = null }) {
  const run = needRun(ctx, runId);
  if (run.status !== 'running') throw new Error('that routine isn’t running');
  const r = needRoutine(ctx, run.routineId);
  const step = stepId ? run.steps.find((s) => s.stepId === stepId) : currentStep(run);
  if (!step || step.doneAt || step.skipped) throw new Error('no step to skip');
  const at = now(ctx);
  const writes = [];
  const task = (ctx.tasks || []).find((t) => t.id === step.taskId);
  if (task) {
    const b = bankStep(ctx, task.id, at);
    if (b.elapsed > 0) writes.push(...stopTask(ctx, { id: task.id, ...b, skip: true, keepTimer: true, keepLock: true }).writes);
    if (b.elapsed > 0 || (task.timeSpent || 0) > 0) writes.push(update(`tasks/${task.id}`, { status: 'done', completedAt: at, skipped: true, updatedAt: at }));
    else writes.push(del(`tasks/${task.id}`));
  }
  const c = advance(ctx, writes);
  const steps = run.steps.map((s) => (s.stepId === step.stepId ? { ...s, skipped: true, doneAt: null } : s));
  writes.push(update(RUN(run.id), { steps, updatedAt: at }));
  return moveOn(c, r, { ...run, steps }, at, writes, [{ when: 'skip', routineId: r.id, runId, stepId: step.stepId }]);
}

/**
 * Back a step: the last step done or skipped goes back on — its task open again (re-made if a skip removed it), the
 * timer on it (the current step's time banked first). A finished run opens again (running) so you can redo its end.
 */
export function routineBackStep(ctx, { runId }) {
  const run = needRun(ctx, runId);
  if (!['running', 'done', 'partial'].includes(run.status)) throw new Error('that routine hasn’t started');
  const r = needRoutine(ctx, run.routineId);
  const cur = run.status === 'running' ? currentStep(run) : null;
  const curIdx = cur ? run.steps.findIndex((x) => x.stepId === cur.stepId) : run.steps.length;
  const prev = [...run.steps.slice(0, curIdx)].reverse().find((x) => x.doneAt || x.skipped);
  if (!prev) throw new Error('already on the first step');
  const at = now(ctx);
  const writes = [];
  let c = ctx;
  // the previous step's task, open again
  const task = (c.tasks || []).find((t) => t.id === prev.taskId);
  if (!task) {
    const made = runTasks(r, run, at).find((t) => t.id === prev.taskId);
    if (made) writes.push(set(`tasks/${made.id}`, made));
  } else if (task.status !== 'todo') {
    writes.push(...reopenTask(c, { id: task.id }).writes, update(`tasks/${task.id}`, { skipped: false }));
  }
  c = advance(c, writes);
  const steps = run.steps.map((x) => {
    if (x.stepId === prev.stepId) return { ...x, doneAt: null, skipped: false, startedAt: at };
    if (cur && x.stepId === cur.stepId) return { ...x, startedAt: null };
    return x;
  });
  const patch = { steps, updatedAt: at };
  if (run.status !== 'running') Object.assign(patch, { status: 'running', finishedAt: null, metrics: null, goalResults: null, pausedAt: null });
  // the clock on the routine's project, the timer on the step (banks whatever the timer held)
  const pid = runProject(r, run);
  let clockWrites;
  if (!c.clock?.active) clockWrites = clockIn(c, { projectId: pid, taskId: prev.taskId }).writes;
  else if ((c.clock.projectId || null) !== pid) clockWrites = switchProject(c, { projectId: pid, taskId: prev.taskId }).writes;
  else clockWrites = startTask(c, { taskId: prev.taskId }).writes;
  writes.push(...clockWrites, update(RUN(run.id), patch));
  return { writes: mergeUpdates(writes), run: { ...run, ...patch }, step: { ...prev, doneAt: null, skipped: false, startedAt: at }, hooks: [{ when: 'back', routineId: r.id, runId, stepId: prev.stepId }] };
}

/**
 * Throw a run away, as if it never happened: the run doc goes, and its step tasks with no time on them. Steps that
 * have time keep their tasks (the time stays logged) but leave the run. A running run is stopped first (finish /
 * back), so the clock is never left on a step that's gone.
 */
export function resetRun(ctx, { runId }) {
  const run = needRun(ctx, runId);
  if (run.status === 'running') throw new Error('that routine is running — finish it first, then reset it');
  const writes = [];
  for (const x of run.steps || []) {
    const t = (ctx.tasks || []).find((k) => k.id === x.taskId);
    if (!t) continue;
    if ((t.timeSpent || 0) > 0 || (t.timeEntries || []).length) writes.push(update(`tasks/${t.id}`, { runId: null, processId: null, processStepId: null, updatedAt: now(ctx) }));
    else writes.push(del(`tasks/${t.id}`));
  }
  writes.push(del(RUN(run.id)));
  return { writes, runId: run.id, kept: writes.filter((w) => w.op === 'update').length, hooks: [{ when: 'reset', routineId: run.routineId, runId: run.id }] };
}

/**
 * A step's task finished somewhere else (its card in the job, the plan, another device): mark the step done (or
 * skipped) from the task — its time becomes the step's (startedAt = doneAt − timeSpent) — and finish the run when
 * nothing is left. Leaves the timer where it is. { writes: [] } when the run already agrees.
 */
export function routineReconcile(ctx, { runId }) {
  const run = needRun(ctx, runId);
  if (run.status !== 'running') return { writes: [], run, changed: false, finished: false, hooks: [] };
  const byId = new Map((ctx.tasks || []).map((t) => [t.id, t]));
  let changed = false;
  const steps = run.steps.map((s) => {
    if (s.doneAt || s.skipped) return s;
    const t = byId.get(s.taskId);
    if (!t || t.status !== 'done') return s;
    changed = true;
    if (t.skipped) return { ...s, skipped: true, doneAt: null };
    const doneAt = t.completedAt || now(ctx);
    return { ...s, doneAt, startedAt: s.startedAt || (t.timeSpent ? doneAt - t.timeSpent : doneAt) };
  });
  if (!changed) return { writes: [], run, changed: false, finished: false, hooks: [] };
  const at = now(ctx);
  const next = { ...run, steps, updatedAt: at };
  const writes = [update(RUN(run.id), { steps, updatedAt: at })];
  if (currentStep(next)) return { writes, run: next, changed: true, finished: false, hooks: [] };
  const f = finishRoutine({ ...ctx, runs: (ctx.runs || []).map((x) => (x.id === run.id ? next : x)) }, { runId: run.id, banked: true });
  return { writes: mergeUpdates([...writes, ...f.writes]), run: f.run, changed: true, finished: true, hooks: f.hooks };
}

/**
 * Something got missed: a new step right after the one you're on (its own task, between its neighbours in the job),
 * and with `keep` the routine gets it too, at the same place, for next time.
 */
export function routineInsertStep(ctx, { runId, title, minutes = null, keep = false }) {
  const run = needRun(ctx, runId);
  if (run.status !== 'running') throw new Error('that routine isn’t running');
  const name = String(title || '').trim();
  if (!name) throw new Error('the step needs a name');
  const r = needRoutine(ctx, run.routineId);
  const at = now(ctx);
  const cur = currentStep(run);
  const idx = cur ? run.steps.findIndex((s) => s.stepId === cur.stepId) + 1 : run.steps.length;
  const stepId = typeof ctx.newId === 'function' ? `s${ctx.newId()}` : `s${at.toString(36)}`;
  const mins = Number(minutes) > 0 ? Math.round(Number(minutes)) : null;
  const st = { stepId, taskId: stepTaskId(run.id, stepId), title: name, minutes: mins, optional: false, capture: null, startedAt: null, doneAt: null, skipped: false, value: null, added: true };
  const steps = [...run.steps.slice(0, idx), st, ...run.steps.slice(idx)];
  // Sort between the neighbouring step tasks so the job's card stack has it in the same place.
  const sortOf = (s) => (ctx.tasks || []).find((t) => t.id === s?.taskId)?.sortOrder;
  const before = sortOf(run.steps[idx - 1]) ?? idx * 1000;
  const after = sortOf(run.steps[idx]) ?? before + 1000;
  const task = makeTaskDoc({
    id: st.taskId, title: name, projectId: runProject(r, run), scheduledDate: run.date, estimatedMinutes: mins,
    sortOrder: (before + after) / 2, processId: r.id, processStepId: stepId, runId: run.id,
  }, [], at);
  const writes = [set(`tasks/${task.id}`, task), update(RUN(run.id), { steps, updatedAt: at })];
  if (keep) {
    const raw = (ctx.routines || []).find((x) => x.id === r.id) || r;
    const list = (raw.steps || []).slice().sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    const pos = cur ? list.findIndex((s) => s.stepId === cur.stepId) + 1 : list.length;
    const at0 = pos > 0 ? pos : list.length;
    const nextList = [...list.slice(0, at0), { stepId, title: name, minutes: mins, optional: false }, ...list.slice(at0)].map((s, i) => ({ ...s, sortOrder: i }));
    writes.push(update(`processes/${r.id}`, { steps: nextList, updatedAt: at }));
  }
  return { writes, run: { ...run, steps }, step: st, kept: !!keep, hooks: [{ when: 'insert', routineId: r.id, runId, stepId }] };
}

/**
 * Which project this run is for (a routine that asks): the run and its step tasks move there, and so does the clock
 * when the routine has it — the session the routine opened is re-filed whole; a clock it switched over is switched
 * again. input = { runId, projectId } (null = no project).
 */
export function routineSetProject(ctx, { runId, projectId = null }) {
  const run = needRun(ctx, runId);
  const r = needRoutine(ctx, run.routineId);
  if (r.projectMode !== 'ask') throw new Error(`${r.name} always goes under its own project — change it on the routine`);
  if (run.status !== 'due' && run.status !== 'running') throw new Error('that routine is already finished');
  const pid = projectId ? String(projectId) : null;
  if (pid && !(ctx.projects || []).some((p) => p.id === pid)) throw new Error(`no project ${pid}`);
  const from = runProject(r, run);
  if (from === pid) return { writes: [], run, changed: false };
  const at = now(ctx);
  const writes = [update(RUN(runId), { projectId: pid, updatedAt: at })];
  const ids = new Set(run.steps.map((s) => s.taskId));
  for (const t of ctx.tasks || []) if (ids.has(t.id)) writes.push(update(`tasks/${t.id}`, { projectId: pid, updatedAt: at }));
  const cl = ctx.clock;
  if (run.status === 'running' && cl?.active && (cl.projectId || null) === from) {
    const session = (ctx.sessions || []).find((x) => x.id === cl.sessionId);
    // The routine opened this session (clocked in, or switched over, when it started): re-file it whole.
    if (session && session.startedAt >= (run.startedAt || at) - 1000) writes.push(...moveSession(ctx, { sessionId: session.id, projectId: pid }).writes);
    else writes.push(...switchProject(ctx, { projectId: pid, taskId: ctx.timer?.taskId || null }).writes);
  }
  return { writes: mergeUpdates(writes), run: { ...run, projectId: pid, updatedAt: at }, changed: true, hooks: [{ when: 'project', routineId: r.id, runId, from, to: pid }] };
}

/** Keep a step's captured value (a number, yes/no, rating, text, photo uri) without finishing it. */
export function routineCapture(ctx, { runId, stepId, value }) {
  const run = needRun(ctx, runId);
  if (!run.steps.some((s) => s.stepId === stepId)) throw new Error(`no step ${stepId}`);
  const steps = run.steps.map((s) => (s.stepId === stepId ? { ...s, value } : s));
  return { writes: [update(RUN(runId), { steps, updatedAt: now(ctx) })], run: { ...run, steps } };
}

/** Leave the routine mode for now: the run is paused, and so is the step's timer. */
export function pauseRoutine(ctx, { runId }) {
  const run = needRun(ctx, runId);
  if (run.status !== 'running' || run.pausedAt) throw new Error('that routine isn’t running');
  const at = now(ctx);
  const writes = [update(RUN(runId), { pausedAt: at, updatedAt: at })];
  const onStep = run.steps.some((s) => s.taskId === ctx.timer?.taskId);
  if (onStep && ctx.timer?.startedAt && !ctx.timer.pauseType) writes.push(...pauseTask(ctx, { type: 'break', context: 'routine' }).writes);
  return { writes, run: { ...run, pausedAt: at } };
}

/** Back into the routine mode: the timer resumes (or goes back onto the current step if it was moved). */
export function resumeRoutine(ctx, { runId }) {
  const run = needRun(ctx, runId);
  if (run.status !== 'running') throw new Error('that routine isn’t running');
  const at = now(ctx);
  const writes = [update(RUN(runId), { pausedAt: null, updatedAt: at })];
  const step = currentStep(run);
  const onStep = step && ctx.timer?.taskId === step.taskId;
  if (onStep && ctx.timer?.pauseType) writes.push(...resumeTask(ctx).writes);
  else if (step && !onStep && (ctx.tasks || []).some((t) => t.id === step.taskId && t.status !== 'done')) {
    if (ctx.clock?.active) writes.push(...startTask(ctx, { taskId: step.taskId }).writes);
    else writes.push(...clockIn(ctx, { projectId: run.projectId || null, taskId: step.taskId }).writes);
  }
  return { writes: mergeUpdates(writes), run: { ...run, pausedAt: null }, step };
}

/**
 * End the run: done when every required step is done, else partial. Metrics + goal results are kept on the run;
 * open steps' untouched tasks are removed; the clock goes back to how it was before the routine.
 * extra = { firstUnlockAt, distractMsBefore } from the phone, when there is one.
 */
export function finishRoutine(ctx, { runId, extra = {}, banked = false }) {
  const run = needRun(ctx, runId);
  if (run.status !== 'running' && run.status !== 'due') throw new Error('that routine is already finished');
  const r = needRoutine(ctx, run.routineId);
  const at = now(ctx);
  const writes = [];
  let c = ctx;
  // bank a step still on the timer
  if (!banked && c.timer?.taskId && run.steps.some((s) => s.taskId === c.timer.taskId)) {
    const b = bankStep(c, c.timer.taskId, at);
    if (b.elapsed > 0) { const w = stopTask(c, { id: c.timer.taskId, ...b, keepLock: true }).writes; writes.push(...w); c = advance(c, w); }
  }
  // untouched open steps: their tasks go
  for (const s of run.steps) {
    if (s.doneAt || s.skipped) continue;
    const t = (c.tasks || []).find((x) => x.id === s.taskId);
    if (t && t.status !== 'done' && !(t.timeSpent > 0)) writes.push(del(`tasks/${t.id}`));
  }
  const status = runOutcome(run);
  const finished = { ...run, status, finishedAt: at, pausedAt: null };
  const metrics = runMetrics(finished, extra);
  const history = (ctx.runs || []).filter((x) => x.routineId === r.id && x.id !== run.id && x.status === 'done' && x.metrics).sort((a, b) => (a.date < b.date ? -1 : 1)).slice(-14).map((x) => x.metrics);
  const goalResults = evaluateRunGoals(r, metrics, history);
  const patch = { status, finishedAt: at, pausedAt: null, metrics, goalResults, updatedAt: at };
  writes.push(update(RUN(run.id), patch));
  // the clock back to how it was
  const pid = runProject(r, run);
  if (run.startedAt && c.clock?.active && (c.clock.projectId || null) === pid) {
    const fresh = { ...c, timer: makeTimerState({ startedAt: at }) };
    const before = run.clockBefore;
    // the task that was on the timer before the routine goes back on it (when it's still open)
    const back = before?.taskId && !run.steps.some((x) => x.taskId === before.taskId) && (c.tasks || []).some((t) => t.id === before.taskId && t.status !== 'done') ? before.taskId : null;
    if (!before) writes.push(...clockOut(fresh, {}).writes);
    else if ((before.projectId || null) !== pid) writes.push(...switchProject(fresh, { projectId: before.projectId, taskId: back }).writes);
    else if (back) writes.push(...startTask(fresh, { taskId: back }).writes);
  }
  return { writes: mergeUpdates(writes), run: { ...finished, ...patch }, hooks: [{ when: 'finish', routineId: r.id, runId: run.id, status }] };
}

/** Not today: the run is kept as skipped with its reason (neutral for the streak); an unstarted run's tasks go. */
export function skipRoutine(ctx, { routineId, date = null, n = 1, reason = null }) {
  const r = needRoutine(ctx, routineId);
  const at = now(ctx);
  const day = date || localDateStr(at);
  const id = runIdFor(r.id, day, n || 1);
  const run = (ctx.runs || []).find((x) => x.id === id);
  if (run && run.status === 'running') throw new Error(`${r.name} is running — finish it instead`);
  if (run && run.status !== 'due') throw new Error(`${r.name} is already ${run.status}`);
  const writes = [];
  if (run) {
    for (const s of run.steps || []) { const t = (ctx.tasks || []).find((x) => x.id === s.taskId); if (t && t.status !== 'done' && !(t.timeSpent > 0)) writes.push(del(`tasks/${t.id}`)); }
    writes.push(update(RUN(id), { status: 'skipped', skipReason: reason || null, finishedAt: at, updatedAt: at }));
  } else {
    const b = buildRun(r, { date: day, n: n || 1, trigger: { type: 'manual', at }, dueAt: at, uid: ctx.uid || null, now: at });
    writes.push(set(RUN(id), { ...b, status: 'skipped', skipReason: reason || null, finishedAt: at }));
  }
  return { writes, runId: id, hooks: [{ when: 'skipRun', routineId: r.id, runId: id, reason }] };
}
